import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "../../context/AuthContext";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Search, Download, Ban, CheckCircle2, Trash2, Loader2 } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { ConfirmAction, fmtDate, Pill } from "./adminUi";

const ROLE_TONE = { seafarer: "navy", company: "orange", institute: "green" };

const toCsv = (users) => {
  const cols = ["name", "email", "phone", "role", "rank", "organization", "job_title", "auth_type", "blocked", "created_at"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.join(","), ...users.map((u) => cols.map((c) => esc(u[c])).join(","))].join("\n");
};

export const AdminUsers = ({ onChanged }) => {
  const { toast } = useToast();
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (query) => {
    setLoading(true);
    try {
      setUsers((await api.get("/admin/users", { params: query ? { q: query } : {} })).data.users);
    } catch (e) {
      toast({ title: "Could not load users", description: formatApiError(e?.response?.data?.detail), variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q, load]);

  const shown = role === "all" ? users : users.filter((u) => (role === "pending" ? !u.role : u.role === role));

  const exportCsv = () => {
    const blob = new Blob([toCsv(shown)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `indosbook-users-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const toggleBlock = async (u) => {
    try {
      await api.put(`/admin/users/${u.user_id}/block`, { blocked: !u.blocked });
      setUsers((l) => l.map((x) => (x.user_id === u.user_id ? { ...x, blocked: !u.blocked } : x)));
      toast({ title: u.blocked ? "User unblocked" : "User blocked" });
      onChanged?.();
    } catch (e) { toast({ title: "Action failed", description: formatApiError(e?.response?.data?.detail), variant: "destructive" }); }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      setUsers((l) => l.filter((x) => x.user_id !== u.user_id));
      toast({ title: "User deleted" });
      onChanged?.();
    } catch (e) { toast({ title: "Delete failed", description: formatApiError(e?.response?.data?.detail), variant: "destructive" }); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="admin-users">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 p-4">
        <div className="flex min-w-[240px] flex-1 items-center gap-2 rounded-full bg-slate-100 px-4">
          <Search className="h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, company…"
            className="h-10 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0" data-testid="admin-users-search" />
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="admin-users-role-filter"
          className="h-10 rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700">
          <option value="all">All roles</option>
          <option value="seafarer">Seafarers</option>
          <option value="company">Companies</option>
          <option value="institute">Institutes</option>
          <option value="pending">Role not chosen</option>
        </select>
        <Button onClick={exportCsv} disabled={!shown.length} data-testid="admin-users-export"
          className="h-10 rounded-full bg-[#002855] px-4 font-bold text-white hover:bg-[#001d40]">
          <Download className="mr-1.5 h-4 w-4" /> Export CSV
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">User</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Sign-up</th>
              <th className="px-4 py-3">Joined</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={6} className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>}
            {!loading && shown.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400" data-testid="admin-users-empty">No users found.</td></tr>}
            {!loading && shown.map((u) => (
              <tr key={u.user_id} className="hover:bg-slate-50/60" data-testid={`admin-user-row-${u.user_id}`}>
                <td className="px-4 py-3">
                  <div className="font-bold text-slate-900">{u.name || "—"}</div>
                  <div className="text-xs text-slate-500">{u.email}{u.phone ? ` · ${u.phone}` : ""}</div>
                  {(u.rank || u.organization) && <div className="text-xs text-slate-400">{u.rank || [u.job_title, u.organization].filter(Boolean).join(" · ")}</div>}
                </td>
                <td className="px-4 py-3">{u.role ? <Pill tone={ROLE_TONE[u.role]}>{u.role}</Pill> : <Pill>pending</Pill>}</td>
                <td className="px-4 py-3"><Pill>{u.auth_type === "google" ? "Google" : "Email"}</Pill></td>
                <td className="whitespace-nowrap px-4 py-3 text-slate-600">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-3">{u.blocked ? <Pill tone="red">Blocked</Pill> : <Pill tone="green">Active</Pill>}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => toggleBlock(u)} data-testid={`admin-user-block-${u.user_id}`}
                      className="h-8 rounded-full px-3 text-xs font-bold">
                      {u.blocked ? <><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Unblock</> : <><Ban className="mr-1 h-3.5 w-3.5" /> Block</>}
                    </Button>
                    <ConfirmAction testid={`admin-user-delete-${u.user_id}`} title={`Delete ${u.name || u.email}?`}
                      description="This permanently removes the account and all their posts, jobs, applications, messages and certificates."
                      confirmLabel="Delete user" onConfirm={() => remove(u)}
                      trigger={<Button variant="outline" data-testid={`admin-user-delete-${u.user_id}`} className="h-8 rounded-full px-3 text-xs font-bold text-red-600 hover:bg-red-50 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></Button>} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border-t border-slate-100 px-4 py-3 text-xs text-slate-500" data-testid="admin-users-count">{shown.length} user{shown.length === 1 ? "" : "s"}</div>
    </div>
  );
};

export default AdminUsers;
